/**
 * cdn-domain-redirects-tab.tsx — Redirects tab for a CDN domain (act-as).
 *
 * Mirrors Partner's RedirectsTab.tsx: DataTable of URL redirect rules with a
 * modal editor that builds a condition array (field + operator + values).
 * PUT sends the full rules array (replace-all semantics).
 *
 * Endpoints: GET/PUT/DELETE /api/v3/cdn/domains/:id/redirects
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Pencil, Trash2, ArrowRight } from 'lucide-react';
import { Section, ActionBar } from '@vnetwork/ui/layout';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  Button,
  IconButton,
  Input,
  Skeleton,
  Badge,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { EmptyState } from '@vnetwork/ui/data';
import { TabError } from '@/pages/customers/detail/tab-states';
import {
  useActAsCDNRedirects,
  actAsCDNRulesKeys,
  type RedirectRule,
  type RedirectCondition,
} from '@/api/act-as-cdn-rules';
import { actAsClient } from '@/api/act-as-client';
import { ActAsMutationConfirm } from '../act-as-mutation-confirm';
import { NumericInput } from '@/components/numeric-input';

/* ── Option lists ────────────────────────────────────────────────────────── */

const STATUS_CODES = [
  { value: '301', label: '301 — Permanent' },
  { value: '302', label: '302 — Found (temporary)' },
  { value: '307', label: '307 — Temporary (method preserved)' },
  { value: '308', label: '308 — Permanent (method preserved)' },
] as const;

const CONDITION_FIELDS = [
  { value: 'url_path', label: 'URL path' },
  { value: 'url_query', label: 'URL query string' },
  { value: 'request_method', label: 'Request method' },
  { value: 'hostname', label: 'Hostname' },
  { value: 'user_agent', label: 'User-Agent' },
  { value: 'cookie', label: 'Cookie' },
  { value: 'origin', label: 'Origin header' },
  { value: 'ip_source', label: 'Source IP' },
];

const CONDITION_OPERATORS = [
  { value: 'equals', label: 'equals' },
  { value: 'equals_ignore_case', label: 'equals (ignore case)' },
  { value: 'not_equals', label: 'not equals' },
  { value: 'not_equals_ignore_case', label: 'not equals (ignore case)' },
  { value: 'starts_with', label: 'starts with' },
  { value: 'starts_with_ignore_case', label: 'starts with (ignore case)' },
  { value: 'not_starts_with', label: 'not starts with' },
  { value: 'ends_with', label: 'ends with' },
  { value: 'ends_with_ignore_case', label: 'ends with (ignore case)' },
  { value: 'not_ends_with', label: 'not ends with' },
  { value: 'contains', label: 'contains' },
  { value: 'not_contains', label: 'not contains' },
  { value: 'regex', label: 'matches regex' },
];

/* ── Local helpers ───────────────────────────────────────────────────────── */

function newRule(): RedirectRule {
  return {
    name: '',
    priority: 10,
    destination_url: '',
    status_code: 302,
    conditions: [],
  };
}

function newCondition(): RedirectCondition {
  return { field: 'url_path', operator: 'starts_with', values: [''] };
}

/* ── Condition row ───────────────────────────────────────────────────────── */

interface ConditionRowProps {
  cond: RedirectCondition;
  onChange: (c: RedirectCondition) => void;
  onRemove: () => void;
}

function ConditionRow({ cond, onChange, onRemove }: ConditionRowProps) {
  const { t } = useTranslation();
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr auto',
        gap: 'var(--vn-space-2)',
        alignItems: 'end',
      }}
    >
      <Field label={t('cdn_domain.redirects.cond_field', 'Field')}>
        {(props) => (
          <Select value={cond.field} onValueChange={(v) => onChange({ ...cond, field: v })}>
            <SelectTrigger {...props}><SelectValue /></SelectTrigger>
            <SelectContent>
              {CONDITION_FIELDS.map(({ value, label }) => (
                <SelectItem key={value} value={value}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </Field>
      <Field label={t('cdn_domain.redirects.cond_operator', 'Operator')}>
        {(props) => (
          <Select value={cond.operator} onValueChange={(v) => onChange({ ...cond, operator: v })}>
            <SelectTrigger {...props}><SelectValue /></SelectTrigger>
            <SelectContent>
              {CONDITION_OPERATORS.map(({ value, label }) => (
                <SelectItem key={value} value={value}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </Field>
      <Field label={t('cdn_domain.redirects.cond_value', 'Value')}>
        {(props) => (
          <Input
            {...props}
            value={cond.values[0] ?? ''}
            placeholder="/old-path"
            onChange={(e) => onChange({ ...cond, values: [e.target.value] })}
          />
        )}
      </Field>
      <IconButton
        variant="ghost"
        size="sm"
        aria-label={t('common.remove', 'Remove')}
        onClick={onRemove}
        style={{ alignSelf: 'flex-end', marginBottom: 2 }}
      >
        <Trash2 size={14} />
      </IconButton>
    </div>
  );
}

/* ── Rule editor modal ───────────────────────────────────────────────────── */

interface RuleEditorProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  initial?: RedirectRule;
  onSave: (rule: RedirectRule) => void;
}

function RuleEditor({ open, onOpenChange, initial, onSave }: RuleEditorProps) {
  const { t } = useTranslation();
  const [rule, setRule] = useState<RedirectRule>(initial ?? newRule());

  useEffect(() => {
    if (open) setRule(initial ?? newRule());
  }, [open, initial]);

  const set = <K extends keyof RedirectRule>(k: K, v: RedirectRule[K]) =>
    setRule((r) => ({ ...r, [k]: v }));

  const addCondition = () => setRule((r) => ({ ...r, conditions: [...r.conditions, newCondition()] }));
  const updateCondition = (idx: number, c: RedirectCondition) =>
    setRule((r) => ({ ...r, conditions: r.conditions.map((x, i) => (i === idx ? c : x)) }));
  const removeCondition = (idx: number) =>
    setRule((r) => ({ ...r, conditions: r.conditions.filter((_, i) => i !== idx) }));

  const valid = rule.destination_url.trim().length > 0 && rule.name.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogHeader>
        {initial
          ? t('cdn_domain.redirects.edit_title', 'Edit redirect rule')
          : t('cdn_domain.redirects.add_title', 'Add redirect rule')}
      </DialogHeader>
      <DialogBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 'var(--vn-space-3)' }}>
          <Field label={t('cdn_domain.redirects.field_name', 'Rule name')}>
            {(props) => (
              <Input
                {...props}
                value={rule.name}
                placeholder="redirect-old-blog"
                onChange={(e) => set('name', e.target.value)}
              />
            )}
          </Field>
          <Field label={t('cdn_domain.redirects.field_priority', 'Priority')}>
            {(props) => (
              <NumericInput
                {...props}
                value={String(rule.priority)}
                onChange={(e) => set('priority', Number(e.target.value) || 0)}
                style={{ maxWidth: 100 }}
              />
            )}
          </Field>
        </div>
        <Field label={t('cdn_domain.redirects.field_destination', 'Destination URL')}>
          {(props) => (
            <Input
              {...props}
              value={rule.destination_url}
              placeholder="https://example.com/new-path"
              onChange={(e) => set('destination_url', e.target.value)}
            />
          )}
        </Field>
        <Field label={t('cdn_domain.redirects.field_status_code', 'Status code')}>
          {(props) => (
            <Select
              value={String(rule.status_code)}
              onValueChange={(v) => set('status_code', parseInt(v, 10) as RedirectRule['status_code'])}
            >
              <SelectTrigger {...props}><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUS_CODES.map(({ value, label }) => (
                  <SelectItem key={value} value={value}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </Field>

        {/* Conditions */}
        <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontWeight: 600, fontSize: 'var(--vn-font-size-sm)' }}>
              {t('cdn_domain.redirects.conditions_label', 'Conditions (AND logic)')}
            </span>
            <Button
              variant="secondary"
              size="sm"
              startIcon={<Plus size={14} />}
              onClick={addCondition}
            >
              {t('cdn_domain.redirects.add_condition', 'Add condition')}
            </Button>
          </div>
          {rule.conditions.length === 0 ? (
            <p style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)', margin: 0 }}>
              {t('cdn_domain.redirects.no_conditions', 'No conditions — rule applies to all requests.')}
            </p>
          ) : (
            rule.conditions.map((c, idx) => (
              <ConditionRow
                key={idx}
                cond={c}
                onChange={(updated) => updateCondition(idx, updated)}
                onRemove={() => removeCondition(idx)}
              />
            ))
          )}
        </div>
      </DialogBody>
      <DialogFooter>
        <Button variant="secondary" onClick={() => onOpenChange(false)}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button
          disabled={!valid}
          onClick={() => { if (valid) { onSave(rule); onOpenChange(false); } }}
        >
          {t('common.save', 'Save')}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

/* ── Tab component ───────────────────────────────────────────────────────── */

interface CdnDomainRedirectsTabProps {
  domainId: string;
}

export function CdnDomainRedirectsTab({ domainId }: CdnDomainRedirectsTabProps) {
  const { t } = useTranslation();
  const q = useActAsCDNRedirects(domainId);

  const [rules, setRules] = useState<RedirectRule[]>([]);
  const [searchText, setSearchText] = useState('');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  useEffect(() => {
    setRules(q.data?.rules ?? []);
  }, [q.data]);

  const openAdd = () => { setEditingIdx(null); setEditorOpen(true); };
  const openEdit = (idx: number) => { setEditingIdx(idx); setEditorOpen(true); };

  const handleSave = (rule: RedirectRule) => {
    setRules((prev) => {
      if (editingIdx !== null) return prev.map((r, i) => (i === editingIdx ? rule : r));
      return [...prev, rule];
    });
  };

  const handleRemove = (idx: number) => {
    setRules((prev) => prev.filter((_, i) => i !== idx));
  };

  const filtered = searchText
    ? rules.filter(
        (r) =>
          r.name.toLowerCase().includes(searchText.toLowerCase()) ||
          r.destination_url.toLowerCase().includes(searchText.toLowerCase()),
      )
    : rules;

  if (q.isLoading) return <Skeleton style={{ height: 240 }} />;
  if (q.isError) {
    return (
      <TabError
        title={t('cdn_domain.redirects.load_error', 'Failed to load redirects')}
        onRetry={() => q.refetch()}
      />
    );
  }

  return (
    <Section
      title={t('cdn_domain.redirects.title', 'Redirects')}
      description={t(
        'cdn_domain.redirects.description',
        'Redirect requests matching rules to a destination URL with a specified status code.',
      )}
    >
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <Card>
          <CardHeader
            actions={
              <Button
                variant="secondary"
                size="sm"
                startIcon={<Plus size={14} />}
                onClick={openAdd}
              >
                {t('cdn_domain.redirects.add_rule', 'Add rule')}
              </Button>
            }
          >
            <CardTitle>{t('cdn_domain.redirects.rules_title', 'Redirect rules')}</CardTitle>
            <CardDescription>
              {t(
                'cdn_domain.redirects.rules_desc',
                'Rules are evaluated in priority order (ascending). All conditions must match (AND logic).',
              )}
            </CardDescription>
          </CardHeader>
          <CardBody style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
            {/* Search filter */}
            {rules.length > 0 && (
              <Input
                value={searchText}
                placeholder={t('cdn_domain.redirects.search_placeholder', 'Search rules…')}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ maxWidth: 340 }}
              />
            )}

            {filtered.length === 0 ? (
              <EmptyState
                size="sm"
                icon={<ArrowRight size={20} />}
                title={
                  rules.length === 0
                    ? t('cdn_domain.redirects.empty_title', 'No redirect rules yet')
                    : t('cdn_domain.redirects.empty_search', 'No rules match your search')
                }
                description={
                  rules.length === 0
                    ? t('cdn_domain.redirects.empty_desc', 'Add a rule to redirect matching requests at the edge.')
                    : undefined
                }
              />
            ) : (
              <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
                {/* Column headers */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '60px 1fr 1fr 80px auto',
                    gap: 'var(--vn-space-2)',
                    padding: '0 var(--vn-space-2)',
                    fontSize: 'var(--vn-font-size-xs)',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    color: 'var(--vn-fg-subtle)',
                  }}
                >
                  <span>{t('cdn_domain.redirects.col_priority', 'Priority')}</span>
                  <span>{t('cdn_domain.redirects.col_name', 'Name')}</span>
                  <span>{t('cdn_domain.redirects.col_destination', 'Destination')}</span>
                  <span>{t('cdn_domain.redirects.col_status', 'Status')}</span>
                  <span />
                </div>
                {filtered.map((rule, idx) => {
                  // Map filtered index back to original index for edits/removes
                  const originalIdx = rules.indexOf(rule);
                  return (
                    <Card
                      flat
                      key={idx}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '60px 1fr 1fr 80px auto',
                        gap: 'var(--vn-space-2)',
                        alignItems: 'center',
                        padding: 'var(--vn-space-2)',
                      }}
                    >
                      <span
                        style={{
                          fontSize: 'var(--vn-font-size-sm)',
                          color: 'var(--vn-fg-muted)',
                        }}
                      >
                        {rule.priority}
                      </span>
                      <span
                        style={{
                          fontSize: 'var(--vn-font-size-sm)',
                          fontWeight: 500,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {rule.name}
                        {rule.conditions.length > 0 && (
                          <Badge
                            tone="neutral"
                            appearance="subtle"
                            size="sm"
                            style={{ marginLeft: 'var(--vn-space-2)' }}
                          >
                            {rule.conditions.length} cond
                          </Badge>
                        )}
                      </span>
                      <span
                        style={{
                          fontFamily: 'var(--vn-font-mono, ui-monospace, monospace)',
                          fontSize: 'var(--vn-font-size-sm)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          color: 'var(--vn-fg-muted)',
                        }}
                      >
                        {rule.destination_url}
                      </span>
                      <Badge tone="info" appearance="subtle" size="sm">
                        {rule.status_code}
                      </Badge>
                      <div style={{ display: 'flex', gap: 'var(--vn-space-1)' }}>
                        <IconButton
                          variant="ghost"
                          size="sm"
                          aria-label={t('common.edit', 'Edit')}
                          onClick={() => openEdit(originalIdx)}
                        >
                          <Pencil size={14} />
                        </IconButton>
                        <IconButton
                          variant="ghost"
                          size="sm"
                          aria-label={t('common.remove', 'Remove')}
                          onClick={() => handleRemove(originalIdx)}
                        >
                          <Trash2 size={14} />
                        </IconButton>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </CardBody>
        </Card>

        <ActionBar
          start={
            q.data ? (
              <Button variant="danger" onClick={() => setDeleteConfirmOpen(true)}>
                {t('cdn_domain.redirects.action_remove', 'Remove config')}
              </Button>
            ) : undefined
          }
          end={
            <Button onClick={() => setConfirmOpen(true)}>
              {t('cdn_domain.redirects.action_save', 'Save')}
            </Button>
          }
        />
      </div>

      <RuleEditor
        open={editorOpen}
        onOpenChange={setEditorOpen}
        initial={editingIdx !== null ? rules[editingIdx] : undefined}
        onSave={handleSave}
      />

      <ActAsMutationConfirm
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={t('cdn_domain.redirects.confirm_save_title', 'Save redirect rules')}
        summary={t(
          'cdn_domain.redirects.confirm_save_summary',
          '{{n}} redirect rule(s) will be applied at the edge.',
          { n: rules.length },
        )}
        queryKeysToInvalidate={[actAsCDNRulesKeys.redirects(domainId)]}
        mutationFn={() => actAsClient.put(`/cdn/domains/${domainId}/redirects`, { rules })}
      />

      <ActAsMutationConfirm
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title={t('cdn_domain.redirects.confirm_delete_title', 'Remove redirects config')}
        summary={t(
          'cdn_domain.redirects.confirm_delete_summary',
          'All redirect rules will be removed.',
        )}
        queryKeysToInvalidate={[actAsCDNRulesKeys.redirects(domainId)]}
        mutationFn={() => actAsClient.delete(`/cdn/domains/${domainId}/redirects`)}
        onSuccess={() => setRules([])}
        destructive
        confirmLabel={t('cdn_domain.redirects.action_remove', 'Remove config')}
      />
    </Section>
  );
}
